import React from 'react'
import moment from 'moment'
import { Avatar, Pane,
   Display4, Body1, majorScale } from '@segment/react-ui-library'
import _ from 'lodash'
import suncalc from 'suncalc'
import Card from './Card'
import Pill from './Pill'
import ShowHide from 'ui/ShowHide'
import GoogleMap from './Map'
import cx from 'classnames'
import UserCardOverlayItem from './UserCardOverlayItem'
import UserCardLocalTime from './UserCardLocalTime'
import SkeletonText from './SkeletonText'
import LocationIcon from '../icons/location'
import EventHistory from '../icons/event-history'
import css from './UserCard.module.css'
import tzlookup from 'tz-lookup'

const calendarFormat = {
  sameDay: '[today at] h:mm A',
  nextDay: '[tomorrow at] h:mm A',
  nextWeek: '[next] dddd [at] h:mm A',
  lastDay: '[yesterday at] h:mm A',
  lastWeek: '[last] dddd [a] h:mm A',
  sameElse: 'DD/MM/YYYY [at] h:mm A'
}

const UserCard = ({ children, avatar, lastSeen, name, id, location, company = 'Unknown Company', role, coordinates = {}, skeleton = false }) => {
  const pastMonth = moment().subtract(30, 'days')
  const lastActive = moment(lastSeen)
  const isActive = lastActive.isAfter(pastMonth)
  const shouldShowMap = Boolean(coordinates.lat && coordinates.lng)
  const useDarkTheme = !isAfterSunriseAndBeforeSunset(coordinates.lat, coordinates.lng)
  const formattedRole = `${_.startCase(role)} at ${company}`

  const localTime = getLocalTime(coordinates.lat, coordinates.lng)

  return (
    <Card position='relative' overflow='hidden' style={{ transform: 'translate3d(0,0,0)' }}>
      <UserCardLocalTime night={useDarkTheme}>
        {skeleton ? <SkeletonText /> : localTime ? localTime.format('h:mm A') : 'Unknown'}
      </UserCardLocalTime>
      <div className={css.mapContainer}>
        <ShowHide show={shouldShowMap}>
          <GoogleMap coordinates={coordinates} useDarkTheme={useDarkTheme} />
        </ShowHide>
      </div>
      <div className={cx({
        [css.userHeader]: true,
        [css.useDarkTheme]: useDarkTheme
      })}>
        <Pane margin={majorScale(2)}>
          <Avatar display='flex' src={avatar} imageSize='40px'>{name}</Avatar>
        </Pane>
        <Pane flex='1'>
          <Pane display='flex' alignItems='center'>
            <Display4 textColor='white' display='inline-block' marginRight='4px'>
              {skeleton ? <SkeletonText /> : name}
            </Display4>
            {!skeleton && isActive ? <div><Pill>Active</Pill></div> : null}
          </Pane>
          <Body1 textColor='white' style={{ marginTop: '-2px', lineHeight: '20px' }}>
            {skeleton ? <SkeletonText /> : formattedRole}
          </Body1>
        </Pane>
        <Pane marginX='24px'>
          <UserCardOverlayItem icon={<LocationIcon />}>
            {skeleton ? <SkeletonText words={2} /> : location || 'Unknown Location'}
          </UserCardOverlayItem>
          <UserCardOverlayItem icon={<EventHistory />}>
            {skeleton ? <SkeletonText words={2} /> : `Last seen ${moment(lastSeen).calendar(null, calendarFormat)}`}
          </UserCardOverlayItem>
        </Pane>
      </div>
      <Pane>
        {children}
      </Pane>
    </Card>
  )
}

function getLocalTime (lat, lng) {
  if (!lat || !lng) return
  const timeZone = tzlookup(lat, lng)
  const localTime = moment().tz(timeZone)
  return localTime
}

/**
 * Check if `lat`/`lng` coordinates is a location that is currently
 * after sunrise and before sunset (i.e. it is light outside.)
 *
 * 1. Look up time zone by `lat`/`lng`.
 * 2. Get current date/time by time zone.
 * 2. Get the `sunrise` and `sunset` date/time.
 * 3. Check that it is both `isAfterSunset` and `isBeforeSunrise`.
 *
 * @param {Number} lat
 * @param {Number} lng
 */

function isAfterSunriseAndBeforeSunset (lat, lng) {
  if (!lat || !lng) return
  const timeZone = tzlookup(lat, lng)
  const localTime = moment().tz(timeZone)
  const { sunrise, sunset } = suncalc.getTimes(localTime, lat, lng)
  const isAfterSunrise = localTime.isAfter(sunrise)
  const isBeforeSunset = localTime.isBefore(sunset)
  return isAfterSunrise && isBeforeSunset
}

export default UserCard



// WEBPACK FOOTER //
// ./components/Customers/presentational/UserCard.js